import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import CustomAutocomplete from '@acnb/CustomComponents/CustomAutocomplete/CustomAutocomplete';
import CmtCard from '@coremat/CmtCard';
import CmtCardContent from '@coremat/CmtCard/CmtCardContent';
import CmtCardHeader from '@coremat/CmtCard/CmtCardHeader';
import GridContainer from '@jumbo/components/GridContainer';
import GridItem from '@jumbo/components/GridItem';
import SearchIcon from '@mui/icons-material/Search';
import { ExpandMore, Visibility } from '@mui/icons-material';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Fab,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { lighten } from '@mui/material/styles';
import moment from 'moment';
import Currency from 'react-currency-formatter';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ThemedTooltip from '@acnb/CustomComponents/Tooltip/ThemedTooltip';
import CartStatusEnum, { CartStatusEnumValue } from 'enums/CartStatusEnum';
import { CriteriaCertificationTypeEnumValue } from 'enums/CriteriaCertificationTypeEnum';
import ExteriorSignageQuoteStatusEnum, { ExteriorSignageQuoteStatusEnumValue } from 'enums/ExteriorSignageQuoteStatusEnum';
import MerchandisingProgramQuoteStatusEnum, {
  MerchandisingProgramQuoteStatusEnumValue,
} from 'enums/MerchandisingProgramQuoteStatusEnum';
import ProjectStatusEnum, { ProjectStatusEnumValue } from 'enums/ProjectStatusEnum';
import { useCarts } from 'services/Data/CartService';
import {
  useDealer,
  useDealers,
  useDsmCertificationReports,
  useDsmDealerComplianceReports,
} from 'services/Data/DealerServices';
import { useExteriorSignageQuotes } from 'services/Data/ExteriorSignageQuoteService';
import { useMerchandisingProgramQuotes } from 'services/Data/MerchandisingProgramQuoteService';
import { useDsmPreviousProjects } from 'services/Data/ProjectServices';
import { useCurrentQuarter } from 'services/Data/QuarterServices';
import useTranslateEnum from 'services/Hooks/useTranslateEnum';
import { translateLabel } from 'services/Utilities/labelUtils';
import {
  formatCertificationLongDate,
  getApprovedCriteriaOptionName,
  getCertifiedUntilDate,
  getCompletedSubmissionCount,
  getExteriorStatusLabel,
  getLatestSubmission,
  getOngoingSubmissionCount,
  getQuarterProgressSummary,
  getSubmissionSentOnLabel,
} from 'services/Utilities/certificationSubmissionSummaryUtils';

const activeRequestParams = {
  pageSize: 100,
  currentPage: 1,
  sorts: [{ column: 'dateCreated', asc: false }],
  filters: [
    {
      selectedColumn: {
        column: {
          filterColumn: 'Status',
          type: 'bool',
        },
      },
      comparator: `{0} >= ${CartStatusEnumValue.PendingBrpApproval}`,
      value: CartStatusEnumValue.PendingBrpApproval,
    },
    {
      selectedColumn: {
        column: {
          filterColumn: 'Status',
          type: 'bool',
        },
      },
      comparator: `{0} < ${CartStatusEnumValue.Shipped}`,
      value: CartStatusEnumValue.PendingBrpApproval,
    },
  ],
};

const activeExteriorQuoteParams = {
  pageSize: 100,
  currentPage: 1,
  sorts: [{ column: 'dateCreated', asc: false }],
  filters: [
    {
      selectedColumn: {
        column: {
          filterColumn: 'Status',
          type: 'bool',
        },
      },
      comparator: `{0} >= ${ExteriorSignageQuoteStatusEnumValue.Submitted}`,
      value: ExteriorSignageQuoteStatusEnumValue.Submitted,
    },
    {
      selectedColumn: {
        column: {
          filterColumn: 'Status',
          type: 'bool',
        },
      },
      comparator: `{0} <= ${ExteriorSignageQuoteStatusEnumValue.PendingPictures}`,
      value: ExteriorSignageQuoteStatusEnumValue.PendingPictures,
    },
  ],
};

const activeMerchandisingQuoteParams = {
  pageSize: 100,
  currentPage: 1,
  sorts: [{ column: 'dateCreated', asc: false }],
  filters: [
    {
      selectedColumn: {
        column: {
          filterColumn: 'Status',
          type: 'bool',
        },
      },
      comparator: `{0} >= ${MerchandisingProgramQuoteStatusEnumValue.InProcess}`,
      value: MerchandisingProgramQuoteStatusEnumValue.InProcess,
    },
    {
      selectedColumn: {
        column: {
          filterColumn: 'Status',
          type: 'bool',
        },
      },
      comparator: `{0} <= ${MerchandisingProgramQuoteStatusEnumValue.Enroll}`,
      value: MerchandisingProgramQuoteStatusEnumValue.Enroll,
    },
  ],
};

const historyRequestParams = {
  pageSize: 100,
  currentPage: 1,
  sorts: [{ column: 'dateCreated', asc: false }],
  filters: [
    {
      selectedColumn: {
        column: {
          filterColumn: 'Status',
          type: 'bool',
        },
      },
      comparator: `{0} > {1}`,
      value: CartStatusEnumValue.Shipped,
    },
  ],
};

const historyExteriorQuoteParams = {
  pageSize: 100,
  currentPage: 1,
  sorts: [{ column: 'dateCreated', asc: false }],
  filters: [
    {
      selectedColumn: {
        column: {
          filterColumn: 'Status',
          type: 'bool',
        },
      },
      comparator: `{0} > {1}`,
      value: ExteriorSignageQuoteStatusEnumValue.PendingPictures,
    },
  ],
};

const historyMerchandisingQuoteParams = {
  pageSize: 100,
  currentPage: 1,
  sorts: [{ column: 'dateCreated', asc: false }],
  filters: [
    {
      selectedColumn: {
        column: {
          filterColumn: 'Status',
          type: 'bool',
        },
      },
      comparator: `{0} > {1}`,
      value: MerchandisingProgramQuoteStatusEnumValue.InProcess,
    },
  ],
};

const getCartStatusDate = cart =>
  cart.cartStatusHistories?.find(history => history.newStatus === CartStatusEnumValue.PendingBrpApproval)
    ?.dateStatusChanged ?? null;

const getExteriorQuoteStatusDate = quote =>
  quote.quoteStatusHistories?.find(history => history.newStatus === ExteriorSignageQuoteStatusEnumValue.Submitted)
    ?.dateStatusChanged ?? quote.dateCreated;

const getMerchandisingQuoteStatusDate = quote =>
  quote.quoteStatusHistories?.find(history => history.newStatus === MerchandisingProgramQuoteStatusEnumValue.InProcess)
    ?.dateStatusChanged ?? quote.dateCreated;

const getProjectStatusDate = project =>
  project.projectStatusHistories?.find(history => history.newStatus === ProjectStatusEnumValue.PendingApproval)
    ?.dateStatusChanged ?? project.dateCreated;

const sortRowsByDateDesc = rows =>
  [...rows].sort((left, right) => {
    const leftDate = left.dateValue ? new Date(left.dateValue).getTime() : 0;
    const rightDate = right.dateValue ? new Date(right.dateValue).getTime() : 0;
    return rightDate - leftDate;
  });

const getOverviewRatio = (value, total) => {
  if (!total) {
    return 0;
  }

  return Math.round((value / total) * 100);
};

const formatReportPercent = (count, total) => {
  if (!Number.isFinite(total) || total <= 0) {
    return '0%';
  }

  return `${Math.round((count / total) * 100)}%`;
};
const reportPlaceholderCount = '-';
const compactReportHeaderStyle = { minHeight: '1rem', maxHeight: '3rem' };
const compactReportContentSx = { p: 2, '&:last-child': { pb: 2 } };
const compactReportTableCellSx = { px: 3, py: 2 };
const dashboardTableScrollSx = { overflowX: 'auto', overflowY: 'auto', maxHeight: '28rem' };

const normalizeSearchText = value =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const filterTableRows = (rows, searchValue, getSearchableValues) => {
  const normalizedSearchValue = normalizeSearchText(searchValue);

  if (!normalizedSearchValue) {
    return rows;
  }

  return rows.filter(row =>
    getSearchableValues(row)
      .map(normalizeSearchText)
      .some(value => value.includes(normalizedSearchValue)),
  );
};

const escapeCsvValue = value => {
  const normalizedValue = value === null || value === undefined ? '' : String(value);
  return `"${normalizedValue.replaceAll('"', '""')}"`;
};

const downloadCsv = (filename, headers, rows) => {
  const csvContent = [headers, ...rows].map(row => row.map(escapeCsvValue).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const ProfileDsmDashboard = () => {
  const { t, i18n } = useTranslation();
  const { translateEnum } = useTranslateEnum();
  const theme = useTheme();
  const accountReducer = useSelector(state => state.AccountReducer);
  const temporaryDealerId = accountReducer?.profile?.temporaryDealer_Id;
  const [expandedSections, setExpandedSections] = useState({
    reports: true,
    certification: true,
    requests: true,
    previousProjects: true,
  });
  const [selectedDealerIds, setSelectedDealerIds] = useState(() => (temporaryDealerId ? [temporaryDealerId] : []));
  const [tableSearchValues, setTableSearchValues] = useState({
    dealerCompliance: '',
    certification: '',
    activeRequests: '',
    historyRequests: '',
    previousProjects: '',
  });

  const availableDealersQuery = useDealers(accountReducer?.isLoggedIn, true);

  const normalizedAvailableDealers = useMemo(() => {
    if (Array.isArray(availableDealersQuery.data)) {
      return availableDealersQuery.data;
    }

    if (Array.isArray(availableDealersQuery.data?.items)) {
      return availableDealersQuery.data.items;
    }

    if (Array.isArray(availableDealersQuery.data?.data)) {
      return availableDealersQuery.data.data;
    }

    return [];
  }, [availableDealersQuery.data]);

  const dealerOptions = useMemo(
    () =>
      normalizedAvailableDealers.map(dealer => ({
        id: dealer.id,
        isRelatedToCurrentUser: dealer.isRelatedToCurrentUser === true,
        name: `${dealer.identifier || ''} - ${dealer.customerName || ''}`,
      })),
    [normalizedAvailableDealers],
  );

  const selectedDealerIdSet = useMemo(() => new Set(selectedDealerIds), [selectedDealerIds]);

  const selectedDealerOptions = useMemo(() => dealerOptions.filter(dealer => selectedDealerIdSet.has(dealer.id)), [
    dealerOptions,
    selectedDealerIdSet,
  ]);

  useEffect(() => {
    if (dealerOptions.length === 0) {
      return;
    }

    setSelectedDealerIds(current => {
      const availableDealerIds = new Set(dealerOptions.map(dealer => dealer.id));
      const nextDealerIds = current.filter(dealerId => availableDealerIds.has(dealerId));

      if (nextDealerIds.length > 0) {
        return nextDealerIds;
      }

      if (temporaryDealerId && availableDealerIds.has(temporaryDealerId)) {
        return [temporaryDealerId];
      }

      return nextDealerIds;
    });
  }, [dealerOptions, temporaryDealerId]);

  const toggleSection = sectionKey => {
    setExpandedSections(current => ({
      ...current,
      [sectionKey]: !current[sectionKey],
    }));
  };

  const updateTableSearchValue = tableKey => event => {
    const { value } = event.target;

    setTableSearchValues(current => ({
      ...current,
      [tableKey]: value,
    }));
  };

  const renderCardToggle = sectionKey => {
    const isExpanded = expandedSections[sectionKey];

    return (
      <IconButton
        aria-expanded={isExpanded}
        aria-label={isExpanded ? t('dsmDashboard.collapseSection') : t('dsmDashboard.expandSection')}
        onClick={() => toggleSection(sectionKey)}
        size="small">
        <ExpandMore
          sx={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: theme.transitions.create('transform', {
              duration: theme.transitions.duration.shortest,
            }),
          }}
        />
      </IconButton>
    );
  };

  const compactReportHeaderBackground = lighten(theme.palette.primary.main, 0.3);

  const compactReportHeaderRowSx = {
    backgroundColor: compactReportHeaderBackground,
    '& .MuiTableCell-root': {
      color: theme.palette.getContrastText(compactReportHeaderBackground),
    },
  };

  const dealerQuery = useDealer();
  const activeCartsQuery = useCarts(activeRequestParams, selectedDealerIds);
  const activeExteriorQuotesQuery = useExteriorSignageQuotes(activeExteriorQuoteParams, selectedDealerIds);
  const activeMerchandisingQuotesQuery = useMerchandisingProgramQuotes(activeMerchandisingQuoteParams, selectedDealerIds);
  const historyCartsQuery = useCarts(historyRequestParams, selectedDealerIds);
  const historyExteriorQuotesQuery = useExteriorSignageQuotes(historyExteriorQuoteParams, selectedDealerIds);
  const historyMerchandisingQuotesQuery = useMerchandisingProgramQuotes(historyMerchandisingQuoteParams, selectedDealerIds);
  const previousProjectsQuery = useDsmPreviousProjects(selectedDealerIds);
  const dealerComplianceReportQuery = useDsmDealerComplianceReports(accountReducer?.isLoggedIn, selectedDealerIds);
  const certificationReportQuery = useDsmCertificationReports(accountReducer?.isLoggedIn, selectedDealerIds);
  const currentQuarterQuery = useCurrentQuarter();

  const renderDealerSelector = () => (
    <CmtCard sx={{ mb: 3 }}>
      <CmtCardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h3" gutterBottom>
              {t('dsmDashboard.dealerSelectorTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('dsmDashboard.dealerSelectorHelperText')}
            </Typography>
          </Box>

          <CustomAutocomplete
            id="dsm-dashboard-dealer-selector"
            labelText={t('dsmDashboard.dealerSelectorLabel')}
            options={dealerOptions}
            formControlProps={{ fullWidth: true }}
            isLoading={availableDealersQuery.isLoading}
            autocompleteProps={{
              multiple: true,
              disableCloseOnSelect: true,
              value: selectedDealerOptions,
              loading: availableDealersQuery.isLoading,
              options: dealerOptions,
              isOptionEqualToValue: (option, value) => option.id === value.id,
              getOptionLabel: option => option.name,
              noOptionsText: t('dsmDashboard.dealerSelectorNoOptions'),
              onChange: (event, value) => setSelectedDealerIds(value.map(dealer => dealer.id)),
              renderOption: (props, option, state, key) => (
                <li key={key} {...props}>
                  <Checkbox checked={state.selected} sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    {option.isRelatedToCurrentUser && (
                      <Typography variant="caption" color="text.secondary">
                        {t('dsmDashboard.dealerSelectorRelatedHint')}
                      </Typography>
                    )}
                  </Box>
                </li>
              ),
            }}
            helperText={t('dsmDashboard.dealerSelectorCaption')}
          />
        </Stack>
      </CmtCardContent>
    </CmtCard>
  );

  const dealerComplianceOverview = dealerComplianceReportQuery.data?.overview;
  const dealerComplianceRequirements = dealerComplianceReportQuery.data?.requirements ?? [];
  const dealerComplianceReportRows = dealerComplianceReportQuery.data?.items ?? [];
  const dealerComplianceOverviewTotal = dealerComplianceOverview?.totalDealers ?? 0;

  const operationStandardComplianceRows = [
    {
      key: 'operation-standard',
      count: dealerComplianceOverview?.operationStandardCompliantDealers ?? reportPlaceholderCount,
      percent: formatReportPercent(
        dealerComplianceOverview?.operationStandardCompliantDealers ?? 0,
        dealerComplianceOverviewTotal,
      ),
      color: 'success',
      messageKey: 'dealer_compliance.overview.operation_standard_compliant_count',
    },
  ];

  const projectCompletedImprovementRequiredRows = [
    {
      key: 'project-completed-improvement-required',
      count: dealerComplianceOverview?.projectCompletedImprovementRequiredDealers ?? reportPlaceholderCount,
      percent: formatReportPercent(
        dealerComplianceOverview?.projectCompletedImprovementRequiredDealers ?? 0,
        dealerComplianceOverviewTotal,
      ),
      color: 'warning',
      messageKey: 'dealer_compliance.overview.operation_standard_non_compliant_count_with_zone',
    },
  ];

  const designRequiredRows = [
    {
      key: 'design-required',
      count: dealerComplianceOverview?.designRequiredDealers ?? reportPlaceholderCount,
      percent: formatReportPercent(dealerComplianceOverview?.designRequiredDealers ?? 0, dealerComplianceOverviewTotal),
      color: 'error',
      messageKey: 'dealer_compliance.overview.operation_standard_non_compliant_count_without_zone',
    },
  ];

  const activeRows = sortRowsByDateDesc([
    ...(activeCartsQuery.data?.items?.map(cart => ({
      id: `cart-${cart.id}`,
      dealerName: cart.dealerName,
      typeLabel: t('dsmDashboard.orderType'),
      number: cart.orderNumber,
      dateValue: getCartStatusDate(cart),
      statusLabel: translateEnum(CartStatusEnum, cart.status),
      total: cart.total,
      currencyName: dealerQuery.data?.currenceName,
      linkTo: `/dealer/profile/awaiting-requests/order/${cart.id}`,
      tooltip: t('see_cart'),
      highlight: cart.status === CartStatusEnumValue.PendingDealerApproval,
    })) ?? []),
    ...(activeExteriorQuotesQuery.data?.items?.map(quote => ({
      id: `exterior-${quote.id}`,
      dealerName: quote.dealerName,
      typeLabel: t('dsmDashboard.exteriorSignageType'),
      number: quote.quoteNumber,
      dateValue: getExteriorQuoteStatusDate(quote),
      statusLabel: translateEnum(ExteriorSignageQuoteStatusEnum, quote.status),
      total: quote.total,
      currencyName: quote.currencyName || dealerQuery.data?.currenceName,
      linkTo: `/dealer/profile/awaiting-requests/exterior-signage-quote/${quote.id}`,
      tooltip: t('see_exterior_signage_quote'),
      highlight: quote.status === ExteriorSignageQuoteStatusEnumValue.PendingDealerApproval,
    })) ?? []),
    ...(activeMerchandisingQuotesQuery.data?.items?.map(quote => ({
      id: `merchandising-${quote.id}`,
      dealerName: quote.dealerName,
      typeLabel: t('dsmDashboard.merchandisingProgramType'),
      number: quote.quoteNumber,
      dateValue: getMerchandisingQuoteStatusDate(quote),
      statusLabel: translateEnum(MerchandisingProgramQuoteStatusEnum, quote.status),
      total: quote.total,
      currencyName: quote.currencyName || dealerQuery.data?.currenceName,
      linkTo: `/dealer/profile/awaiting-requests/merchandising-program-quote/${quote.id}`,
      tooltip: t('see_merchandising_program_quote'),
      highlight: false,
    })) ?? []),
  ]);

  const historyRows = sortRowsByDateDesc([
    ...(historyCartsQuery.data?.items?.map(cart => ({
      id: `history-cart-${cart.id}`,
      dealerName: cart.dealerName,
      typeLabel: t('dsmDashboard.orderType'),
      number: cart.orderNumber,
      dateValue: getCartStatusDate(cart),
      statusLabel: translateEnum(CartStatusEnum, cart.status),
      total: cart.total,
      currencyName: dealerQuery.data?.currenceName,
      linkTo: `/dealer/profile/request-histories/order/${cart.id}`,
      tooltip: t('see_order'),
    })) ?? []),
    ...(historyExteriorQuotesQuery.data?.items?.map(quote => ({
      id: `history-exterior-${quote.id}`,
      dealerName: quote.dealerName,
      typeLabel: t('dsmDashboard.exteriorSignageType'),
      number: quote.quoteNumber,
      dateValue: getExteriorQuoteStatusDate(quote),
      statusLabel: translateEnum(ExteriorSignageQuoteStatusEnum, quote.status),
      total: quote.total,
      currencyName: quote.currencyName || dealerQuery.data?.currenceName,
      linkTo: `/dealer/profile/request-histories/exterior-signage-quote/${quote.id}`,
      tooltip: t('see_exterior_signage_quote'),
    })) ?? []),
    ...(historyMerchandisingQuotesQuery.data?.items?.map(quote => ({
      id: `history-merchandising-${quote.id}`,
      dealerName: quote.dealerName,
      typeLabel: t('dsmDashboard.merchandisingProgramType'),
      number: quote.quoteNumber,
      dateValue: getMerchandisingQuoteStatusDate(quote),
      statusLabel: translateEnum(MerchandisingProgramQuoteStatusEnum, quote.status),
      total: quote.total,
      currencyName: quote.currencyName || dealerQuery.data?.currenceName,
      linkTo: `/dealer/profile/request-histories/merchandising-program-quote/${quote.id}`,
      tooltip: t('see_merchandising_program_quote'),
    })) ?? []),
  ]);

  const previousProjects = sortRowsByDateDesc(
    (previousProjectsQuery.data ?? []).map(project => ({
      id: project.id,
      dealerName: project.dealer?.customerName,
      projectNumber: project.projectNumber,
      dateValue: getProjectStatusDate(project),
      targetedShippingDate: project.targetedShippingDate,
      contactName: project.contactName,
      brpContact: project.userFullName,
      statusLabel: translateEnum(ProjectStatusEnum, project.status),
      linkTo: `/dealer/profile/previous-projects/${project.id}`,
    })),
  );

  const isLoadingActive =
    activeCartsQuery.isLoading ||
    activeCartsQuery.isFetching ||
    activeExteriorQuotesQuery.isLoading ||
    activeExteriorQuotesQuery.isFetching ||
    activeMerchandisingQuotesQuery.isLoading ||
    activeMerchandisingQuotesQuery.isFetching ||
    dealerQuery.isLoading;

  const isLoadingHistory =
    historyCartsQuery.isLoading ||
    historyCartsQuery.isFetching ||
    historyExteriorQuotesQuery.isLoading ||
    historyExteriorQuotesQuery.isFetching ||
    historyMerchandisingQuotesQuery.isLoading ||
    historyMerchandisingQuotesQuery.isFetching;

  const getComplianceResultLabel = result => {
    if (!result) {
      return t('dealer_compliance.not_available');
    }

    if (result.isCompliant) {
      return t('compliance_validation.result_compliant');
    }

    if (result.isEvaluated === false) {
      return t('compliance_validation.result_not_evaluated');
    }

    return t('compliance_validation.result_non_compliant');
  };

  const getRequirementLabel = requirement =>
    translateLabel(requirement?.nameLabel, i18n.language) || requirement?.internalName || '';

  const handleExportDealerComplianceCsv = () => {
    const columns = [
      {
        label: t('dsmDashboard.dealerComplianceReport.dealerNumber'),
        getValue: row => row.dealerNumber,
      },
      {
        label: t('dsmDashboard.dealerComplianceReport.dealerName'),
        getValue: row => row.dealerName,
      },
      {
        label: t('dsmDashboard.dealerComplianceReport.operationStandardCompliant'),
        getValue: row => t(row.isOperationStandardCompliant ? 'yes' : 'no'),
      },
      {
        label: t('certification_tool'),
        getValue: row => row.certificationToolResult?.displayValue || t('compliance_validation.result_non_compliant'),
      },
      {
        label: t('dsmDashboard.dealerComplianceReport.flooring'),
        getValue: row => getComplianceResultLabel(row.flooringResult),
      },
      {
        label: t('dsmDashboard.dealerComplianceReport.paint'),
        getValue: row => getComplianceResultLabel(row.paintResult),
      },
      ...dealerComplianceRequirements.map(requirement => ({
        label: getRequirementLabel(requirement),
        getValue: row => getComplianceResultLabel(row.complianceResults?.[requirement.id]),
      })),
    ];

    downloadCsv(
      `dealer-compliance-${new Date().toISOString().slice(0, 10)}.csv`,
      columns.map(column => column.label),
      dealerComplianceReportRows.map(row => columns.map(column => column.getValue(row))),
    );
  };

  const getComplianceChipSx = result => {
    if (!result) {
      return {
        backgroundColor: theme.palette.grey[200],
        color: theme.palette.text.secondary,
      };
    }

    if (result.isEvaluated === false) {
      return {
        backgroundColor: theme.palette.action.disabledBackground,
        color: theme.palette.text.secondary,
      };
    }

    return {
      backgroundColor: result.isCompliant ? theme.palette.success.light : theme.palette.error.light,
      color: theme.palette.getContrastText(result.isCompliant ? theme.palette.success.light : theme.palette.error.light),
    };
  };

  const renderComplianceResultCell = result => (
    <Chip
      label={getComplianceResultLabel(result)}
      size="small"
      title={result?.summary || t('dealer_compliance.no_result')}
      sx={{ fontWeight: 700, maxWidth: '100%', ...getComplianceChipSx(result) }}
    />
  );

  const renderCertificationToolCell = result => (
    <Chip
      label={result?.displayValue || t('compliance_validation.result_non_compliant')}
      size="small"
      title={result?.summary || t('dealer_compliance.no_result')}
      sx={{ fontWeight: 700, maxWidth: '100%', ...getComplianceChipSx(result) }}
    />
  );

  const renderOperationStandardCell = value => (
    <Chip
      label={t(value ? 'yes' : 'no')}
      size="small"
      sx={{
        backgroundColor: value ? theme.palette.success.light : theme.palette.error.light,
        color: theme.palette.getContrastText(value ? theme.palette.success.light : theme.palette.error.light),
        fontWeight: 700,
      }}
    />
  );

  const renderTableSearch = (tableKey, sx = {}) => (
    <Box sx={{ mb: 2, width: '100%', maxWidth: 440, ...sx }}>
      <TextField
        fullWidth
        size="small"
        value={tableSearchValues[tableKey]}
        onChange={updateTableSearchValue(tableKey)}
        placeholder={t('dsmDashboard.tableSearchPlaceholder')}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: theme.palette.text.secondary }} />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.background.paper,
          },
        }}
      />
    </Box>
  );

  const getNoSearchResultsMessage = () => t('dsmDashboard.noSearchResults');

  const formatDateCell = dateValue => (dateValue ? moment(dateValue).format(t('date_format')) : '-');

  const certificationReportRows = (certificationReportQuery.data ?? []).map(row => ({
    ...row,
    certificationName: getCertificationReportName(row.certificationType),
    ongoingSubmissionCount: getOngoingSubmissionCount(row.submissions),
    completedSubmissionCount: getCompletedSubmissionCount(row.submissions),
    details: getCertificationReportDetails(row),
  }));

  const filteredActiveRows = filterTableRows(activeRows, tableSearchValues.activeRequests, row => [
    row.dealerName,
    row.typeLabel,
    row.number,
    formatDateCell(row.dateValue),
    row.statusLabel,
    row.total,
    row.currencyName,
  ]);

  const filteredHistoryRows = filterTableRows(historyRows, tableSearchValues.historyRequests, row => [
    row.dealerName,
    row.typeLabel,
    row.number,
    formatDateCell(row.dateValue),
    row.statusLabel,
    row.total,
    row.currencyName,
  ]);

  const filteredPreviousProjects = filterTableRows(previousProjects, tableSearchValues.previousProjects, project => [
    project.dealerName,
    project.projectNumber,
    formatDateCell(project.dateValue),
    formatDateCell(project.targetedShippingDate),
    project.contactName,
    project.brpContact,
    project.statusLabel,
  ]);

  const filteredDealerComplianceRows = filterTableRows(
    dealerComplianceReportRows,
    tableSearchValues.dealerCompliance,
    row => [
      row.dealerNumber,
      row.dealerName,
      row.isOperationStandardCompliant ? t('yes') : t('no'),
      row.certificationToolResult?.displayValue || t('compliance_validation.result_non_compliant'),
      getComplianceResultLabel(row.flooringResult),
      getComplianceResultLabel(row.paintResult),
      ...dealerComplianceRequirements.map(requirement => getComplianceResultLabel(row.complianceResults?.[requirement.id])),
    ],
  );

  const filteredCertificationReportRows = filterTableRows(certificationReportRows, tableSearchValues.certification, row => [
    row.dealerNumber,
    row.dealerName,
    row.certificationName,
    row.ongoingSubmissionCount,
    row.completedSubmissionCount,
    row.details,
  ]);

  const activeRequestsEmptyMessage = tableSearchValues.activeRequests
    ? getNoSearchResultsMessage()
    : t('dsmDashboard.noActiveRequests');

  const historyRequestsEmptyMessage = tableSearchValues.historyRequests
    ? getNoSearchResultsMessage()
    : t('dsmDashboard.noHistoryRequests');

  const isLoadingComplianceOverview = dealerComplianceReportQuery.isLoading || dealerComplianceReportQuery.isFetching;

  const renderCardLoadingState = ({ lines = 4, showHeader = false } = {}) => (
    <Stack spacing={1.5}>
      {showHeader && (
        <SkeletonWrapper isLoading variant="text" skeletonProps={{ width: '55%', height: 32 }} component={<span />} />
      )}
      {Array.from({ length: lines }).map((_, index) => (
        <SkeletonWrapper
          key={`dashboard-loading-line-${lines}-${index}`}
          isLoading
          variant="rounded"
          skeletonProps={{ width: '100%', height: index === 0 ? 42 : 26 }}
          component={<span />}
        />
      ))}
    </Stack>
  );

  const renderOverviewCardLoadingState = () => (
    <Stack spacing={1.5}>
      <SkeletonWrapper isLoading variant="text" skeletonProps={{ width: '35%', height: 56 }} component={<span />} />
      <SkeletonWrapper isLoading variant="text" skeletonProps={{ width: '65%', height: 24 }} component={<span />} />
      <SkeletonWrapper isLoading variant="rounded" skeletonProps={{ width: '100%', height: 10 }} component={<span />} />
      <SkeletonWrapper isLoading variant="text" skeletonProps={{ width: '80%', height: 24 }} component={<span />} />
    </Stack>
  );

  const activeRequestsContent = isLoadingActive
    ? renderCardLoadingState({ lines: 5 })
    : renderRequestTable(filteredActiveRows, activeRequestsEmptyMessage);

  const historyRequestsContent = isLoadingHistory
    ? renderCardLoadingState({ lines: 5 })
    : renderRequestTable(filteredHistoryRows, historyRequestsEmptyMessage);

  function renderRequestTable(rows, emptyMessage) {
    if (rows.length === 0) {
      return <Typography>{emptyMessage}</Typography>;
    }

    return (
      <Box sx={dashboardTableScrollSx}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('dealer')}</TableCell>
              <TableCell>{t('dsmDashboard.requestType')}</TableCell>
              <TableCell>{t('dsmDashboard.referenceNumber')}</TableCell>
              <TableCell>{t('date')}</TableCell>
              <TableCell>{t('status')}</TableCell>
              <TableCell>{t('total')}</TableCell>
              <TableCell>{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.id} style={row.highlight ? { backgroundColor: '#f6d5d5' } : undefined}>
                <TableCell>{row.dealerName}</TableCell>
                <TableCell>{row.typeLabel}</TableCell>
                <TableCell>{row.number}</TableCell>
                <TableCell>{formatDateCell(row.dateValue)}</TableCell>
                <TableCell>{row.statusLabel}</TableCell>
                <TableCell>
                  <Currency quantity={row.total} currency={row.currencyName} />
                </TableCell>
                <TableCell>
                  <ThemedTooltip title={row.tooltip} placement={'top'}>
                    <Link to={row.linkTo}>
                      <Fab
                        color="primary"
                        sx={{
                          height: 26,
                          width: 26,
                          minHeight: 26,
                          '& svg': {
                            height: 14,
                            width: 14,
                          },
                        }}>
                        <Visibility />
                      </Fab>
                    </Link>
                  </ThemedTooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  }

  const renderProjectsTable = () => {
    if (previousProjects.length === 0) {
      return <Typography>{t('dsmDashboard.noPreviousProjects')}</Typography>;
    }

    if (filteredPreviousProjects.length === 0) {
      return <Typography>{getNoSearchResultsMessage()}</Typography>;
    }

    return (
      <Box sx={dashboardTableScrollSx}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('dealer')}</TableCell>
              <TableCell>{t('dsmDashboard.projectNumber')}</TableCell>
              <TableCell>{t('date')}</TableCell>
              <TableCell>{t('targetedShippingDate')}</TableCell>
              <TableCell>{t('dealerContact')}</TableCell>
              <TableCell>{t('brpContact')}</TableCell>
              <TableCell>{t('status')}</TableCell>
              <TableCell>{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPreviousProjects.map(project => (
              <TableRow key={project.id}>
                <TableCell>{project.dealerName}</TableCell>
                <TableCell>{project.projectNumber}</TableCell>
                <TableCell>{formatDateCell(project.dateValue)}</TableCell>
                <TableCell>{formatDateCell(project.targetedShippingDate)}</TableCell>
                <TableCell>{project.contactName}</TableCell>
                <TableCell>{project.brpContact}</TableCell>
                <TableCell>{project.statusLabel}</TableCell>
                <TableCell>
                  <ThemedTooltip title={t('see_project')} placement={'top'}>
                    <Link to={project.linkTo}>
                      <Fab
                        color="primary"
                        sx={{
                          height: 26,
                          width: 26,
                          minHeight: 26,
                          '& svg': {
                            height: 14,
                            width: 14,
                          },
                        }}>
                        <Visibility />
                      </Fab>
                    </Link>
                  </ThemedTooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };
  const renderComplianceOverviewCard = card => {
    const numericCount = Number.isFinite(Number(card.count)) ? Number(card.count) : 0;
    const progressValue = getOverviewRatio(numericCount, dealerComplianceOverviewTotal);

    return (
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '2.6rem', md: '2.3rem', lg: '2.5rem' },
            }}>
            {card.count}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('dealer_compliance.overview.dealers_count', {
              count: numericCount,
              total: dealerComplianceOverviewTotal,
            })}
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
          <Box />
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {card.percent}
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progressValue}
          color={card.color}
          sx={{ height: 10, borderRadius: 999 }}
        />
        <Typography variant="body2" color="text.primary">
          {t(card.messageKey || 'dealer_compliance.overview.percent_of_filtered', {
            percent: progressValue,
          })}
        </Typography>
      </Stack>
    );
  };

  const renderDealerComplianceReportTable = () => {
    if (dealerComplianceReportQuery.isLoading || dealerComplianceReportQuery.isFetching) {
      return renderCardLoadingState({ lines: 6, showHeader: true });
    }

    if (!dealerComplianceReportRows.length) {
      return <Typography>{t('dsmDashboard.noDealerComplianceReportRows')}</Typography>;
    }

    if (!filteredDealerComplianceRows.length) {
      return <Typography>{getNoSearchResultsMessage()}</Typography>;
    }

    return (
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={compactReportHeaderRowSx}>
              <TableCell sx={compactReportTableCellSx} colSpan={2}>
                {t('dsmDashboard.dealerComplianceReport.dealerInformations')}
              </TableCell>
              <TableCell sx={compactReportTableCellSx} colSpan={4}>
                {t('dsmDashboard.dealerComplianceReport.coreCompliance')}
              </TableCell>
              <TableCell sx={compactReportTableCellSx} colSpan={dealerComplianceRequirements.length + 2}>
                {t('dsmDashboard.dealerComplianceReport.missingRequirements')}
              </TableCell>
            </TableRow>
            <TableRow sx={compactReportHeaderRowSx}>
              <TableCell sx={compactReportTableCellSx}>{t('dsmDashboard.dealerComplianceReport.dealerNumber')}</TableCell>
              <TableCell sx={compactReportTableCellSx}>{t('dsmDashboard.dealerComplianceReport.dealerName')}</TableCell>
              <TableCell sx={compactReportTableCellSx} align="center">
                {t('dsmDashboard.dealerComplianceReport.operationStandardCompliant')}
              </TableCell>
              <TableCell sx={compactReportTableCellSx}>{t('certification_tool')}</TableCell>
              <TableCell sx={compactReportTableCellSx}>{t('dsmDashboard.dealerComplianceReport.flooring')}</TableCell>
              <TableCell sx={compactReportTableCellSx}>{t('dsmDashboard.dealerComplianceReport.paint')}</TableCell>
              {dealerComplianceRequirements.map(requirement => (
                <TableCell key={requirement.id} sx={compactReportTableCellSx}>
                  {getRequirementLabel(requirement)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDealerComplianceRows.map(row => (
              <TableRow key={row.dealerNumber}>
                <TableCell sx={compactReportTableCellSx}>{row.dealerNumber}</TableCell>
                <TableCell sx={compactReportTableCellSx}>{row.dealerName}</TableCell>
                <TableCell sx={compactReportTableCellSx} align="center">
                  {renderOperationStandardCell(row.isOperationStandardCompliant)}
                </TableCell>
                <TableCell sx={compactReportTableCellSx}>
                  {renderCertificationToolCell(row.certificationToolResult)}
                </TableCell>
                <TableCell sx={compactReportTableCellSx}>{renderComplianceResultCell(row.flooringResult)}</TableCell>
                <TableCell sx={compactReportTableCellSx}>{renderComplianceResultCell(row.paintResult)}</TableCell>
                {dealerComplianceRequirements.map(requirement => (
                  <TableCell key={requirement.id} sx={compactReportTableCellSx}>
                    {renderComplianceResultCell(row.complianceResults?.[requirement.id])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  function getCertificationReportName(certificationType) {
    if (Number(certificationType) === CriteriaCertificationTypeEnumValue.ExteriorSignage) {
      return t('home.exteriorSignage');
    }

    return t('home.retailEnvironment');
  }

  function getCertificationReportDetails(row) {
    const latestSubmission = getLatestSubmission(row.submissions);
    const approvedOptionName = getApprovedCriteriaOptionName(latestSubmission, i18n.language);

    if (Number(row.certificationType) === CriteriaCertificationTypeEnumValue.RetailEnvironment) {
      const certifiedUntilDate = getCertifiedUntilDate(row.submissions);
      const certifiedUntilLabel = certifiedUntilDate
        ? t('home.certifiedUntil', {
            certifiedUntil: formatCertificationLongDate(certifiedUntilDate, i18n.language),
          })
        : null;

      const notStartedProgressLabel =
        !certifiedUntilLabel &&
        getCompletedSubmissionCount(row.submissions) === 0 &&
        getOngoingSubmissionCount(row.submissions) === 0
          ? getQuarterProgressSummary(currentQuarterQuery.data, i18n.language, t)
          : null;

      return [certifiedUntilLabel, approvedOptionName, notStartedProgressLabel].filter(Boolean).join(' - ') || '-';
    }

    const statusLabel = getExteriorStatusLabel(latestSubmission, i18n.language, t);
    const submissionSentOnLabel = getSubmissionSentOnLabel(latestSubmission, i18n.language, t);

    return [statusLabel, approvedOptionName, submissionSentOnLabel].filter(Boolean).join(' - ') || '-';
  }

  const renderCertificationReportTable = () => {
    if (certificationReportQuery.isLoading || certificationReportQuery.isFetching) {
      return renderCardLoadingState({ lines: 5, showHeader: true });
    }

    if (!certificationReportQuery.data?.length) {
      return <Typography>{t('dsmDashboard.noCertificationReportRows')}</Typography>;
    }

    if (!filteredCertificationReportRows.length) {
      return <Typography>{getNoSearchResultsMessage()}</Typography>;
    }

    return (
      <Box sx={dashboardTableScrollSx}>
        <Table size="small">
          <TableHead>
            <TableRow sx={compactReportHeaderRowSx}>
              <TableCell sx={compactReportTableCellSx}>{t('dsmDashboard.certificationReport.dealerName')}</TableCell>
              <TableCell sx={compactReportTableCellSx}>{t('dsmDashboard.certificationReport.certificationName')}</TableCell>
              <TableCell sx={compactReportTableCellSx}>{t('dsmDashboard.certificationReport.ongoingSubmission')}</TableCell>
              <TableCell sx={compactReportTableCellSx}>
                {t('dsmDashboard.certificationReport.completedSubmission')}
              </TableCell>
              <TableCell sx={compactReportTableCellSx}>{t('dsmDashboard.certificationReport.details')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCertificationReportRows.map(row => (
              <TableRow key={`${row.dealerNumber}-${row.certificationType}`}>
                <TableCell sx={compactReportTableCellSx}>{row.dealerName}</TableCell>
                <TableCell sx={compactReportTableCellSx}>{row.certificationName}</TableCell>
                <TableCell sx={compactReportTableCellSx}>{row.ongoingSubmissionCount}</TableCell>
                <TableCell sx={compactReportTableCellSx}>{row.completedSubmissionCount}</TableCell>
                <TableCell sx={compactReportTableCellSx}>{row.details}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  return (
    <GridContainer>
      <GridItem xs={12}>{renderDealerSelector()}</GridItem>

      <GridItem xs={12}>
        <CmtCard>
          <CmtCardHeader
            style={{ minHeight: '60px' }}
            separator={{ color: theme.palette.borderColor.dark }}
            title={t('dsmDashboard.complianceReportsTitle')}>
            {renderCardToggle('reports')}
          </CmtCardHeader>
          <Collapse in={expandedSections.reports} timeout="auto" unmountOnExit>
            <CmtCardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <GridContainer>
                <GridItem xs={12} md={4}>
                  <CmtCard sx={{ height: '100%' }}>
                    <CmtCardHeader
                      style={compactReportHeaderStyle}
                      separator={{ color: theme.palette.borderColor.dark }}
                      title={t('dealer_compliance.overview.operation_standard_compliant_title')}
                    />
                    <CmtCardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                      {isLoadingComplianceOverview
                        ? renderOverviewCardLoadingState()
                        : renderComplianceOverviewCard(operationStandardComplianceRows[0])}
                    </CmtCardContent>
                  </CmtCard>
                </GridItem>

                <GridItem xs={12} md={4}>
                  <CmtCard sx={{ height: '100%' }}>
                    <CmtCardHeader
                      style={compactReportHeaderStyle}
                      separator={{ color: theme.palette.borderColor.dark }}
                      title={t('dealer_compliance.overview.project_completed_improvement_required_title')}
                    />
                    <CmtCardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                      {isLoadingComplianceOverview
                        ? renderOverviewCardLoadingState()
                        : renderComplianceOverviewCard(projectCompletedImprovementRequiredRows[0])}
                    </CmtCardContent>
                  </CmtCard>
                </GridItem>

                <GridItem xs={12} md={4}>
                  <CmtCard sx={{ height: '100%' }}>
                    <CmtCardHeader
                      style={compactReportHeaderStyle}
                      separator={{ color: theme.palette.borderColor.dark }}
                      title={t('dealer_compliance.overview.design_required_title')}
                    />
                    <CmtCardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                      {isLoadingComplianceOverview
                        ? renderOverviewCardLoadingState()
                        : renderComplianceOverviewCard(designRequiredRows[0])}
                    </CmtCardContent>
                  </CmtCard>
                </GridItem>

                <GridItem xs={12}>
                  <Box sx={{ mt: 2 }}>
                    <CmtCard>
                      <CmtCardHeader
                        style={compactReportHeaderStyle}
                        separator={{ color: theme.palette.borderColor.dark }}
                        title={t('dsmDashboard.dealerComplianceReport.title')}
                      />
                      <CmtCardContent sx={compactReportContentSx}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
                          {renderTableSearch('dealerCompliance', { mb: 0 })}
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleExportDealerComplianceCsv}
                            disabled={isLoadingComplianceOverview || !dealerComplianceReportRows.length}
                            sx={{ whiteSpace: 'nowrap' }}>
                            {t('dsmDashboard.dealerComplianceReport.exportCsv')}
                          </Button>
                        </Stack>
                        {renderDealerComplianceReportTable()}
                      </CmtCardContent>
                    </CmtCard>
                  </Box>
                </GridItem>
              </GridContainer>
            </CmtCardContent>
          </Collapse>
        </CmtCard>
      </GridItem>

      <GridItem xs={12} md={6}>
        <CmtCard>
          <CmtCardHeader
            style={{ minHeight: '60px' }}
            separator={{ color: theme.palette.borderColor.dark }}
            title={t('dsmDashboard.certificationTitle')}>
            {renderCardToggle('certification')}
          </CmtCardHeader>
          <Collapse in={expandedSections.certification} timeout="auto" unmountOnExit>
            <CmtCardContent>
              {renderTableSearch('certification')}
              {renderCertificationReportTable()}
            </CmtCardContent>
          </Collapse>
        </CmtCard>
      </GridItem>
      <GridItem xs={12} md={6}>
        <CmtCard>
          <CmtCardHeader
            style={{ minHeight: '60px' }}
            separator={{ color: theme.palette.borderColor.dark }}
            title={t('dsmDashboard.activeRequestsTitle')}>
            {renderCardToggle('requests')}
          </CmtCardHeader>
          <Collapse in={expandedSections.requests} timeout="auto" unmountOnExit>
            <CmtCardContent>
              <Typography variant="h4" gutterBottom>
                {t('dsmDashboard.activeRequestsDescription')}
              </Typography>
              {renderTableSearch('activeRequests')}
              {activeRequestsContent}
            </CmtCardContent>

            <CmtCardHeader
              style={{ minHeight: '60px' }}
              separator={{ color: theme.palette.borderColor.dark }}
              title={t('dsmDashboard.historyTitle')}
            />
            <CmtCardContent>
              <Typography variant="h4" gutterBottom>
                {t('dsmDashboard.historyDescription')}
              </Typography>
              {renderTableSearch('historyRequests')}
              {historyRequestsContent}
            </CmtCardContent>
          </Collapse>
        </CmtCard>
      </GridItem>

      <GridItem xs={12}>
        <CmtCard>
          <CmtCardHeader
            style={{ minHeight: '60px' }}
            separator={{ color: theme.palette.borderColor.dark }}
            title={t('dsmDashboard.previousProjectsTitle')}>
            {renderCardToggle('previousProjects')}
          </CmtCardHeader>
          <Collapse in={expandedSections.previousProjects} timeout="auto" unmountOnExit>
            <CmtCardContent>
              <Typography variant="h4" gutterBottom>
                {t('dsmDashboard.previousProjectsDescription')}
              </Typography>
              {renderTableSearch('previousProjects')}
              {previousProjectsQuery.isLoading || previousProjectsQuery.isFetching
                ? renderCardLoadingState({ lines: 5, showHeader: true })
                : renderProjectsTable()}
            </CmtCardContent>
          </Collapse>
        </CmtCard>
      </GridItem>
    </GridContainer>
  );
};

export default ProfileDsmDashboard;
